import autoComplete from "@tarekraafat/autocomplete.js";

/**
 * AutoComplete - A custom class for implementing autocomplete functionality.
 * This class uses the autoComplete.js library to provide dynamic autocomplete
 * functionality to an input field. The input field fetches suggestions from a server
 * based on the user’s query and displays them in a dropdown.
 */
class AutoComplete {
    /**
     * Constructor for the AutoComplete class.
     * Initializes the autoComplete.js instance.
     */
    constructor() {
        this.autoCompleteInstance = null;
    }

    /**
     * Initializes the autocomplete functionality on the input element.
     * This method binds the input field to the autoComplete.js library, sets up the data
     * source, and adds event listeners for various events like onOpen, onSelect, etc.
     *
     * #### EXAMPLE
     * ```javascript
     * const autoCompleteInstance = new AutoComplete();
     * autoCompleteInstance.add("#autoComplete", url, { every option of autocomplete.js })
     * ```
     *
     * @param {string} selector - The selector to apply autocomplete functionality. It can either be a selector string.
     * @param {string} url - The URL to fetch the autocomplete suggestions from the server.
     * @param {object} [options={}] - The custom options to modify the default behavior of autocomplete.
     * @param {function} [options.onSelect=(event, details)=>{}] Event listener for the `onOpen` event. This is triggered when the autocomplete dropdown is opened.
     * @param {function} [options.onInitialize=(event, details)=>{}] Fires after the autoComplete.js engine is initialized and ready.
     * @param {function} [options.onOpen=(event, details)=>{}] Event listener for the `onOpen` event. This is triggered when the autocomplete dropdown is opened.
     * @param {function} [options.onClose=(event, details)=>{}] Event listener for the `onClose` event. This is triggered when the autocomplete dropdown is closed.
     * @param {function} [options.onClear=(event, details)=>{}] Fires when the input is cleared.
     * @param {function} [options.onNavigate=(event, details)=>{}] Fires every time a user navigates through the results list.
     * @param {function} [options.onResultsShow=(event, details)=>{}] Fires when the results are shown after fetching data.
     * @param {function} [options.onResponse=(event, details)=>{}] Fires after the data is fetched and the response is ready.
     * @returns {void}
     */
    add(selector, url, options = {}) {

        const inputElement = selector instanceof HTMLElement ? selector : document.querySelector(selector);

        if (!inputElement) {
            console.error(`Element with selector ${selector} not found.`);
            return;
        }


        /**
         * Default options for autoComplete.js.
         * These options define the minimum number of characters before autocomplete suggestions are triggered.
         */
        const defaultOptions = {
            threshold: 2,  // Minimum number of characters required before suggestions are triggered.
            wrapper: false, // If true, wraps results list inside a custom wrapper element.
            debounce: 300,  // Delay (in ms) before sending the request after user input.
            data: {
                src: async (query) => {
                    try {
                        const delimiter = url.includes('?') ? '&' : '?';
                        const source = await fetch(url + delimiter + "term=" + query);
                        const data = await source.json();
                        return Array.isArray(data) ? data : []; // Return the data if it's an array, else return an empty array.
                    } catch (error) {
                        console.error("Error fetching data:", error);
                        return []; // Return an empty array in case of an error.
                    }
                },
                keys: ["value"]
            },
            resultsList: {
                element: (list, data) => {
                    if (data.results.length === 0) {
                        const info = document.createElement('li');
                        info.innerHTML = `No results found for <strong>"${data.query}"</strong>`;
                        list.prepend(info);
                    }
                },
                id: selector + "-lists",
                class: "results-list",
                destination: selector,
                noResults: true,
                maxResults: 10,
            }
        };

        // Merge default options with passed custom options
        const finalOptions = { ...defaultOptions, ...options };

        // Initialize the autoComplete.js instance
        this.autoCompleteInstance = new autoComplete({
            selector: selector,
            ...finalOptions, // Spread the final options to apply to autoComplete.js instance
        });

        /**
         * Event listener for the `onOpen` event. This is triggered when the autocomplete dropdown is opened.
         *
         * @param {Event} event - The open event triggered by the autoComplete.js.
         * @param {object} details - Additional details about the event, provided by autoComplete.js.
         */
        inputElement.addEventListener("open", function (event) {
            const inputWidth = event.target.offsetWidth;
            const resultsList = document.getElementById(selector + "-lists");

            // Apply max-width to the results list
            if (resultsList) {
                resultsList.style.maxWidth = `${inputWidth}px`;
            }

            if (typeof finalOptions.onOpen === "function") {
                finalOptions.onOpen(event, event.detail);
            }
        });


        /**
         * Event listener for the `onSelect` event. This is triggered when a user selects an item from the dropdown.
         *
         * @param {Event} event - The selection event triggered by the autoComplete.js.
         * @param {object} details - The details of the selected item, including the value.
         */
        inputElement.addEventListener("selection", function (event) {
            const selectedItem = event.detail.selection.value; // Get the selected item's value.
            this.value = selectedItem.url; // Set the selected value (e.g., URL) in the input field.
            if (typeof finalOptions.onSelect === "function") {
                finalOptions.onSelect(event, event.detail);  // Trigger the custom onSelect event handler
            }
        });

        /**
         * Event listener for the `onClose` event. This is triggered when the autocomplete dropdown is closed.
         *
         * @param {Event} event - The close event triggered by the autoComplete.js.
         * @param {object} details - Additional details about the event, provided by autoComplete.js.
         */
        if (typeof finalOptions.onClose === "function") {
            inputElement.addEventListener("close", function (event) {
                finalOptions.onClose(event, event.detail);
            });
        }

        /**
         * Fires after the autoComplete.js engine is initialized and ready.
         *
         * @param {Event} event - The init event triggered by autoComplete.js.
         * @param {object} details - Additional details about the initialization event.
         */
        if (typeof finalOptions.onInitialize === "function") {
            inputElement.addEventListener("init", function (event) {
                finalOptions.onInitialize(event, event.detail);
            });
        }

        /**
         * Fires after the data is fetched and the response is ready.
         *
         * @param {Event} event - The response event triggered by autoComplete.js.
         * @param {object} details - The fetched data, provided by autoComplete.js.
         */
        if (typeof finalOptions.onResponse === "function") {
            inputElement.addEventListener("response", function (event) {
                finalOptions.onResponse(event, event.detail);
            });
        }

        /**
         * Fires when the results are shown after fetching data.
         *
         * @param {Event} event - The results event triggered by autoComplete.js.
         * @param {object} details - The result details, provided by autoComplete.js.
         */
        if (typeof finalOptions.onResultsShow === "function") {
            inputElement.addEventListener("results", function (event) {
                finalOptions.onResultsShow(event, event.detail);
            });
        }

        /**
         * Fires every time a user navigates through the results list.
         *
         * @param {Event} event - The navigate event triggered by autoComplete.js.
         * @param {object} details - The navigation details, provided by autoComplete.js.
         */
        if (typeof finalOptions.onNavigate === "function") {
            inputElement.addEventListener("navigate", function (event) {
                finalOptions.onNavigate(event, event.detail);
            });
        }

        /**
         * Fires when the input is cleared.
         *
         * @param {Event} event - The clear event triggered by autoComplete.js.
         * @param {object} details - The clear event details.
         */
        if (typeof finalOptions.onClear === "function") {
            inputElement.addEventListener("clear", function (event) {
                finalOptions.onClear(event, event.detail);
            });
        }
    }
}

export default AutoComplete;
